import classNames from "classnames";
import makeStyles from '@mui/styles/makeStyles';
import {CalendarDays, Plus, X} from "lucide-react";
import {get, isEmpty, map, take} from "lodash";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {slotLabel} from "business/callSlots";
import {color, font, radius, type} from "common/tokens";
import Card from "./Card";
import EventSeriesDialog, {singleOption} from "components/EventSeriesDialog";
import AddSlotDialog from "./AddSlotDialog";
import IstNote from "./IstNote";

const useStyles = makeStyles({
  card: {
    padding: 17,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    background: color.ACCENT_TINT,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  heading: {
    ...type.cardHeading,
    color: color.TEXT,
    margin: 0,
  },
  icon: {
    color: color.ACCENT_TEXT,
    display: 'flex',
    flexShrink: 0,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    ...type.meta,
    fontWeight: font.SEMIBOLD,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 12px',
    borderRadius: radius.PILL,
    background: color.SURFACE,
    border: `1px solid ${color.CARD_BORDER}`,
    color: color.TEXT,
  },
  chipRemove: {
    appearance: 'none',
    border: 'none',
    padding: 0,
    background: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: color.TEXT_MUTED,
    '&:disabled': {
      cursor: 'default',
      opacity: 0.35,
    },
  },
  suggestion: {
    ...type.meta,
    fontWeight: font.SEMIBOLD,
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '7px 12px',
    borderRadius: radius.PILL,
    background: 'none',
    border: `1px dashed ${color.ACCENT}`,
    color: color.ACCENT_TEXT,
    cursor: 'pointer',
    '&:disabled': {
      cursor: 'default',
      opacity: 0.5,
    },
  },
  moreToggle: {
    borderStyle: 'solid',
  },
  addSlot: {
    width: '100%',
    justifyContent: 'center',
    marginTop: 16,
    padding: '10px 12px',
  },
  error: {
    ...type.meta,
    color: color.DANGER,
    margin: 0,
  },
  footnote: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    margin: 0,
  },
});

const VISIBLE_SUGGESTIONS = 2;

const CallSlotsCard = ({callSlots, onAdd, onRemove}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showAllSuggested, setShowAllSuggested] = useState(false);
  const [removing, setRemoving] = useState(null);

  const slots = get(callSlots, 'slots', []);
  if (isEmpty(slots)) {
    return null;
  }

  const suggested = get(callSlots, 'suggestedSlots', []);
  const canEdit = get(callSlots, 'canEdit', false);
  const hiddenCount = suggested.length - VISIBLE_SUGGESTIONS;
  const visibleSuggested = showAllSuggested ? suggested : take(suggested, VISIBLE_SUGGESTIONS);

  const run = async action => {
    setBusy(true);
    setFailed(false);
    try {
      await action();
    } catch (e) {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const add = slot => run(() => onAdd(slot));

  return (
    <Card bordered className={classes.card}>
      <div className={classes.header}>
        <h3 className={classes.heading}>{t('yourMentoringCallSlots')}</h3>
        <span className={classes.icon}>
          <CalendarDays size={18} strokeWidth={2}/>
        </span>
      </div>

      <div className={classes.chips}>
        {map(slots, slot => (
          <span key={slot.id} className={classes.chip}>
            {slotLabel(slot)}
            {canEdit && (
              <button
                type={'button'}
                className={classes.chipRemove}
                aria-label={t('removeSlot', {slot: slotLabel(slot)})}
                disabled={busy || slots.length === 1}
                onClick={() => setRemoving(slot)}>
                <X size={13} strokeWidth={2.25}/>
              </button>
            )}
          </span>
        ))}
        {canEdit && map(visibleSuggested, slot => (
          <button
            type={'button'}
            key={slot.id}
            className={classes.suggestion}
            disabled={busy}
            onClick={() => add({weekday: slot.weekday, hour: slot.hour, minute: slot.minute || 0})}>
            <Plus size={13} strokeWidth={2.5}/>
            {slotLabel(slot)}
          </button>
        ))}
        {canEdit && hiddenCount > 0 && !showAllSuggested && (
          <button type={'button'} className={classNames(classes.suggestion, classes.moreToggle)}
                  onClick={() => setShowAllSuggested(true)}>
            {t('moreSlots', {count: hiddenCount})}
          </button>
        )}
      </div>
      {canEdit && (
        <button type={'button'} className={classNames(classes.suggestion, classes.addSlot)} disabled={busy}
                onClick={() => setAdding(true)}>
          <Plus size={13} strokeWidth={2.5}/>
          {t('addSlot')}
        </button>
      )}

      <EventSeriesDialog
        open={Boolean(removing)}
        title={t('removeSlotTitle')}
        prompt={t('removeSlotMessage', {slot: removing && slotLabel(removing)})}
        options={singleOption(t('removeLabel'))}
        chosenVariant={'danger'}
        onChoose={() => {
          const slotId = removing.id;
          setRemoving(null);
          run(() => onRemove(slotId));
        }}
        onDismiss={() => setRemoving(null)}
      />

      {canEdit && adding && (
        <AddSlotDialog
          weekdays={get(callSlots, 'pickableWeekdays', [])}
          existingSlots={slots}
          onClose={() => setAdding(false)}
          onAdd={onAdd}/>
      )}

      {failed && <p className={classes.error}>{t('slotUpdateFailed')}</p>}
      <IstNote/>
      {canEdit && <p className={classes.footnote}>{t('callSlotsFootnote')}</p>}
    </Card>
  );
};

export default CallSlotsCard;
